import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { SnackbarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { UuidUtilitiesService } from '@utilities/uuid-utilities.service';

/** Navigation state key marking a redirect as guard-initiated, as opposed to a directly-entered URL. */
export const INTERNAL_REDIRECT_STATE_KEY = 'internalRedirect';

export interface IValidUuidParamGuardData {
  /** Route param name to validate as a UUID v4. Defaults to 'id'. */
  uuidParamName?: string;
  /** Path to redirect to when the param is not a valid UUID v4. If omitted, the user stays on the current route. */
  uuidRedirectTo?: string;
  /** Optional translation key passed via navigation state (not the URL) to the redirect target. */
  uuidRedirectTitleKey?: string;
  /** Whether to show the invalid-id error snackbar. Defaults to true. */
  uuidShowSnackbar?: boolean;
}

/**
 * Ensures a route param (by default `id`) is a valid UUID v4.
 * Shows an error snackbar when it is not, and redirects to the configured
 * path if `uuidRedirectTo` is set. If `uuidRedirectTo` is omitted, the user
 * stays on the current route (useful when there is no sensible parent page).
 *
 * Usage in routes:
 *
 * {
 *   path: 'creators/:id',
 *   component: CreatorDetailsComponent,
 *   canActivate: [validUuidParamGuard],
 *   data: { uuidRedirectTo: 'creators' },
 * }
 */
export const validUuidParamGuard: CanActivateFn = (route: ActivatedRouteSnapshot): boolean => {
  const router = inject(Router);
  const snackbarService = inject(SnackbarService);
  const translate = inject(TranslateService);
  const data = route.data as IValidUuidParamGuardData;
  const { uuidParamName = 'id', uuidRedirectTo, uuidRedirectTitleKey, uuidShowSnackbar = true } = data;
  const paramValue = route.paramMap.get(uuidParamName);

  if (paramValue && UuidUtilitiesService.isUuidV4(paramValue)) {
    return true;
  }

  if (uuidShowSnackbar) {
    snackbarService.openError(translate.instant('shared.invalidUrlId'));
  }

  if (!uuidRedirectTo) {
    return true;
  }

  router.navigate([uuidRedirectTo], {
    state: {
      [INTERNAL_REDIRECT_STATE_KEY]: true,
      ...(uuidRedirectTitleKey ? { titleKey: uuidRedirectTitleKey } : {}),
    },
  });

  return false;
};
